import { Compound } from '../../services/restserver-openapi';

export type AgentCuration = {
    curationId: number;
    curationName: string;
    description: string;
    author: string;
    visible: boolean;
    compoundIds: number[];
    image?: string | null;
}

const normalizeCuration = (curation: Partial<AgentCuration>): AgentCuration => ({
    curationId: curation.curationId ?? 0,
    curationName: curation.curationName ?? '',
    description: curation.description ?? '',
    author: curation.author ?? '',
    visible: curation.visible ?? false,
    compoundIds: curation.compoundIds ?? [],
    image: curation.image ?? null,
});

const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token');

    return {
        'Content-Type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
    };
};

const getApiUrl = (path: string) => `${process.env.REACT_APP_API_URL ?? ''}${path}`;

let inFlightGetCurationsAll: Promise<AgentCuration[]> | null = null;
let inFlightGetCurationsVisible: Promise<AgentCuration[]> | null = null;

async function parseJsonResponse<T>(response: Response, fallback?: T): Promise<T> {
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text
            ? `Request failed with status ${response.status}: ${text}`
            : `Request failed with status ${response.status}`);
    }

    const text = await response.text();
    if (!text) {
        if (typeof fallback !== 'undefined') {
            return fallback;
        }
        throw new Error('Request succeeded but response body was empty.');
    }

    try {
        return JSON.parse(text) as T;
    } catch (error) {
        if (typeof fallback !== 'undefined') {
            return fallback;
        }
        throw error;
    }
}

export async function getCurations(visibleOnly = false): Promise<AgentCuration[]> {
    const inFlight = visibleOnly ? inFlightGetCurationsVisible : inFlightGetCurationsAll;
    if (inFlight) {
        return inFlight;
    }

    const endpoint = visibleOnly ? '/curations?visibleOnly=true' : '/curations';
    const request = (async () => {
        const response = await fetch(getApiUrl(endpoint), {
            headers: getAuthHeaders(),
        });

        const curations = await parseJsonResponse<AgentCuration[]>(response, []);
        return curations.map((curation) => normalizeCuration(curation));
    })();

    if (visibleOnly) {
        inFlightGetCurationsVisible = request;
    } else {
        inFlightGetCurationsAll = request;
    }

    try {
        return await request;
    } finally {
        if (visibleOnly) {
            inFlightGetCurationsVisible = null;
        } else {
            inFlightGetCurationsAll = null;
        }
    }
}

export async function createCuration(curation: AgentCuration): Promise<AgentCuration> {
    const response = await fetch(getApiUrl('/curations'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(curation),
    });

    const saved = await parseJsonResponse<AgentCuration>(response, curation);
    return normalizeCuration(saved);
}

export async function updateCuration(curation: AgentCuration): Promise<AgentCuration> {
    const response = await fetch(getApiUrl(`/curations/${curation.curationId}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(curation),
    });

    const saved = await parseJsonResponse<AgentCuration>(response, curation);
    return normalizeCuration(saved);
}

export async function deleteCuration(curationId: number): Promise<void> {
    const response = await fetch(getApiUrl(`/curations/${curationId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        throw new Error(`Delete failed with status ${response.status}`);
    }
}

export async function addCompoundToCuration(curationId: number, compoundId: number): Promise<AgentCuration> {
    const response = await fetch(getApiUrl(`/curations/${curationId}/compounds/${compoundId}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
    });

    const saved = await parseJsonResponse<AgentCuration>(response);
    return normalizeCuration(saved);
}

export async function removeCompoundFromCuration(curationId: number, compoundId: number): Promise<AgentCuration> {
    const response = await fetch(getApiUrl(`/curations/${curationId}/compounds/${compoundId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });

    const saved = await parseJsonResponse<AgentCuration>(response);
    return normalizeCuration(saved);
}

export function getCompoundLabel(compound: Compound): string {
    return `${compound.name} (#${compound.compoundId})`;
}
